import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Shield, CheckCircle, Upload, 
  Lock, Zap, Check, Tablet, Smartphone, Sparkles
} from 'lucide-react';

const NextGenFormsSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[80px] translate-x-1/2 translate-y-1/2" />

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10">
            {/* Left Side: Context */}
            <div className="w-[40%] flex flex-col justify-center space-y-8">
                
                <div className="space-y-4">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200">
                        <FileText className="w-4 h-4" />
                        <span>Next-Gen Forms</span>
                    </div>
                    
                    <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 leading-tight">
                        Modern, Secure Forms <span className="text-blue-600">That Patients Actually Complete</span>.
                    </h2>
                    
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Replace clunky PDFs with beautiful, mobile-friendly forms that have higher completion rates. 
                        Forms are sent via secure links and automatically uploaded to the correct folder in the patient's chart.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="p-2 bg-green-100 rounded-lg text-green-600 mt-1">
                            <Lock className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">Secure & HIPAA Compliant</h4>
                            <p className="text-sm text-slate-500">Encrypted links ensure patient data is always protected.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600 mt-1">
                            <Upload className="w-5 h-5" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 text-lg">Auto-PMS Upload</h4>
                            <p className="text-sm text-slate-500">Signed forms are instantly saved to the patient's document center.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Graphic */}
            <div className="w-[60%] h-full flex items-center justify-center relative">
                
                {/* Floating Devices Composition */}
                <div className="relative w-full max-w-2xl h-[500px] flex items-center justify-center">
                    
                    {/* Tablet View (Back) */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, x: 40 }}
                        whileInView={{ opacity: 1, scale: 1, x: 40 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="absolute right-0 top-10 w-[400px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 transform rotate-6 block"
                    >
                        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex gap-2">
                            <div className="w-2 h-2 rounded-full bg-slate-300" />
                            <div className="w-2 h-2 rounded-full bg-slate-300" />
                        </div>
                        <div className="p-6 space-y-4 opacity-50">
                            <div className="h-6 w-1/2 bg-slate-200 rounded" />
                            <div className="space-y-2">
                                <div className="h-4 w-full bg-slate-100 rounded" />
                                <div className="h-4 w-3/4 bg-slate-100 rounded" />
                            </div>
                        </div>
                    </motion.div>

                    {/* Mobile View (Front & Center) */}
                    <motion.div 
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="relative z-20 w-[320px] bg-white rounded-[2.5rem] shadow-2xl border-8 border-slate-900 overflow-hidden"
                    >
                        {/* Status Bar */}
                        <div className="bg-slate-900 text-white px-6 py-3 flex justify-between items-center text-xs">
                            <span>9:41</span>
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 bg-white rounded-full opacity-20" />
                                <div className="w-3 h-3 bg-white rounded-full opacity-20" />
                                <div className="w-3 h-3 bg-white rounded-full" />
                            </div>
                        </div>

                        {/* App Header */}
                        <div className="bg-white px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                            <div className="font-bold text-slate-900">Asha Health</div>
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xs">
                                1/4
                            </div>
                        </div>

                        {/* Form Content */}
                        <div className="p-6 bg-slate-50 min-h-[400px]">
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-1">Patient Intake</h3>
                                    <p className="text-xs text-slate-500">Fast, secure, and easy.</p>
                                </div>

                                <div className="space-y-4">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-700">Full Name</label>
                                        <div className="h-10 bg-white rounded-lg border border-blue-200 shadow-sm flex items-center px-3 text-sm text-slate-900">
                                            Sarah Johnson
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-700">Insurance Provider</label>
                                        <div className="h-10 bg-white rounded-lg border border-slate-200 shadow-sm flex items-center px-3 text-sm text-slate-400">
                                            Select Provider...
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-700">Medical History</label>
                                        <div className="flex gap-2">
                                            <div className="flex-1 h-20 bg-white rounded-lg border border-slate-200 shadow-sm p-2" />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <div className="w-full h-11 bg-blue-600 rounded-xl shadow-lg shadow-blue-200 flex items-center justify-center text-white font-semibold text-sm">
                                        Continue
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Floating Success Notification */}
                    <motion.div 
                        initial={{ scale: 0, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.6, type: "spring" }}
                        className="absolute -right-8 top-1/2 bg-white px-4 py-3 rounded-xl shadow-xl border border-green-100 flex items-center gap-3 z-30"
                    >
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-green-200">
                            <Check className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-slate-900">Sync Complete</div>
                            <div className="text-[10px] text-slate-500">Uploaded to Dentrix</div>
                        </div>
                    </motion.div>

                    {/* Decorative Elements */}
                    <div className="absolute -left-10 top-20 w-16 h-16 bg-blue-500/10 rounded-full blur-xl animate-pulse" />
                    <div className="absolute right-10 bottom-20 w-24 h-24 bg-indigo-500/10 rounded-full blur-xl animate-pulse delay-700" />
                </div>
            </div>
        </div>
    </div>
  );
};

export default NextGenFormsSlide;
